import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createBloodResult } from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Calendar, Edit, Activity, X, Camera, Thermometer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { UNITS } from '../utils/unitFormatter';
import { getLocalTodayISO, formatDisplayDate } from '../utils/dateUtils';
import PhotoScanBloodResults from '../components/PhotoScanBloodResults';

const StepByStepBloodEntry = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [showScanner, setShowScanner] = useState(
    () => new URLSearchParams(window.location.search).get('scan') === '1'
  );
  const [formData, setFormData] = useState({
    date: getLocalTodayISO(),
    haemoglobin: '',
    wbc: '',
    platelets: '',
    neutrophils: '',
    lymphocytes: '',
    is_during_illness: false
  });
  const [confidenceData, setConfidenceData] = useState(null); // Store scan confidence info
  const [scanHbUnit, setScanHbUnit] = useState(null); // Unit detected on scanned report for haemoglobin (g/dL or g/L)

  // CBC Marker definitions using centralized unit formatter
  const markers = [
    {
      step: 2,
      field: 'haemoglobin',
      label: 'Haemoglobin',
      unit: user?.country === 'US' ? UNITS.HEMOGLOBIN_US : UNITS.HEMOGLOBIN_SI,
      range: user?.country === 'US' ? '13.0-18.0' : '130-180'
    },
    {
      step: 3,
      field: 'wbc',
      label: 'White Blood Cells',
      unit: UNITS.BLOOD_COUNT,
      range: '4.0-11.0'
    },
    {
      step: 4,
      field: 'platelets',
      label: 'Platelets',
      unit: UNITS.BLOOD_COUNT,
      range: '150-400'
    },
    {
      step: 5,
      field: 'neutrophils',
      label: 'Neutrophils',
      unit: UNITS.BLOOD_COUNT,
      range: '2.0-7.5'
    },
    {
      step: 6,
      field: 'lymphocytes',
      label: 'Lymphocytes',
      unit: UNITS.BLOOD_COUNT,
      range: '1.0-4.0'
    }
  ];

  const totalSteps = markers.length + 2; // Date + CBC markers + review

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    handleNext();
  };

  const handleSubmit = async () => {
    try {
      // Build payload with only non-empty values
      const payload = {
        date: formData.date,
        is_during_illness: formData.is_during_illness || false
      };

      // Add only markers that have values
      Object.keys(formData).forEach(key => {
        if (key !== 'date' && key !== 'is_during_illness' && formData[key] !== '') {
          payload[key] = parseFloat(formData[key]);
        }
      });

      // Scan-sourced haemoglobin: send the unit detected on the report so the
      // backend converts it exactly once (skips country-based normalisation)
      if (payload.haemoglobin !== undefined && scanHbUnit) {
        payload.haemoglobin_unit = scanHbUnit;
      }

      // Validate at least one marker is entered
      if (Object.keys(payload).length <= 2) { // date + is_during_illness
        toast.error('Please enter at least one blood marker value');
        return;
      }

      await createBloodResult(payload);
      toast.success('Blood test saved successfully');
      navigate('/blood-results');
    } catch (error) {
      toast.error('Failed to save blood test');
      console.error('Error:', error);
    }
  };

  const handleEditMarker = (markerStep) => {
    setCurrentStep(markerStep);
  };

  const handleCancel = () => {
    // Navigate back without confirmation for better mobile UX
    navigate('/blood-results');
  };

  // Handle scan completion
  const handleScanComplete = (extractedData) => {
    setShowScanner(false);
    setConfidenceData(extractedData.confidence);
    setScanHbUnit(extractedData.haemoglobin && extractedData.haemoglobin_unit ? extractedData.haemoglobin_unit : null);
    setFormData({
      ...formData,
      date: extractedData.date || formData.date,
      wbc: extractedData.wbc?.toString() || '',
      lymphocytes: extractedData.lymphocytes?.toString() || '',
      platelets: extractedData.platelets?.toString() || '',
      haemoglobin: extractedData.haemoglobin?.toString() || '',
      neutrophils: extractedData.neutrophils?.toString() || ''
    });
    // Jump to review step after scanning
    setCurrentStep(totalSteps);
  };

  const getCurrentMarker = () => {
    return markers.find(m => m.step === currentStep);
  };

  // Show the scanned report's unit for haemoglobin when the value came from a scan
  const getMarkerUnit = (marker) => {
    if (marker.field === 'haemoglobin' && scanHbUnit) return scanHbUnit;
    return marker.unit;
  };

  const currentMarker = getCurrentMarker();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      <div className="max-w-md mx-auto px-4 py-6 sm:py-8 w-full box-border">

        {/* Progress Indicator */}
        {currentStep <= totalSteps && (
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-sm text-slate-600">
                Step {currentStep} of {totalSteps}
              </span>
            </div>
            <div className="flex gap-1">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-all ${
                    i + 1 <= currentStep ? 'bg-primary' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Photo Scanner */}
        {showScanner && (
          <div className="w-full">
            <PhotoScanBloodResults
              onScanComplete={handleScanComplete}
              onCancel={() => setShowScanner(false)}
            />
          </div>
        )}

        {/* Step 1: Select Test Date */}
        {currentStep === 1 && !showScanner && (
          <div className="space-y-6 w-full">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                <Calendar className="h-7 w-7 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Add Blood Results</h1>
              <p className="text-sm text-slate-600">Enter your blood test results manually or scan a report</p>
            </div>

            {/* Scan Report Button */}
            <button
              onClick={() => setShowScanner(true)}
              className="w-full flex items-center justify-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:from-blue-100 hover:to-indigo-100 transition-all"
              data-testid="scan-report-button"
            >
              <Camera className="h-6 w-6 text-blue-600" />
              <span className="font-semibold text-blue-700">Scan a pathology report</span>
            </button>

            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-sm text-slate-500">or enter manually</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-slate-200 w-full overflow-hidden">
              <div className="w-full">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Test Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  max={getLocalTodayISO()}
                  className="w-full text-lg font-semibold text-slate-900 bg-slate-50 rounded-xl px-3 py-3 border-2 border-slate-200 focus:border-primary outline-none transition-colors text-center box-border"
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-2">
                  Make sure this matches the date on your pathology report.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                onClick={handleCancel}
                className="flex-1 min-w-0 text-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 min-w-0 text-sm"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Steps 2-10: Marker Entry */}
        {currentStep >= 2 && currentStep <= markers.length + 1 && currentMarker && (
          <div className="space-y-6 w-full">
            {/* Test Date Display */}
            <div className="bg-primary/5 rounded-xl p-3 border border-primary/20">
              <div className="flex items-center justify-center gap-2 text-primary">
                <Calendar className="h-4 w-4 flex-shrink-0" />
                <span className="text-sm font-medium">
                  Test Date: {formatDisplayDate(formData.date, user?.country || 'AU')}
                </span>
              </div>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-3">
                <Activity className="h-7 w-7 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{currentMarker.label}</h1>
              <p className="text-sm text-slate-600">Enter the value from your test results</p>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-slate-200 w-full overflow-hidden">
              <div className="w-full">
                {/* Value Input */}
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Value (optional)
                </label>
                <div className="w-full">
                  <input
                    type="number"
                    step="0.1"
                    value={formData[currentMarker.field]}
                    onChange={(e) => setFormData({ ...formData, [currentMarker.field]: e.target.value })}
                    className="w-full text-2xl font-semibold text-slate-900 bg-slate-50 rounded-xl px-3 py-3 border-2 border-slate-200 focus:border-primary outline-none transition-colors text-center box-border"
                    placeholder="0.0"
                    autoFocus
                  />
                  <p className="text-sm text-slate-500 mt-2 text-center">
                    {currentMarker.unit} • Range: {currentMarker.range}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="flex-1 min-w-0 text-sm px-2"
              >
                <ArrowLeft className="h-4 w-4 mr-1 flex-shrink-0" />
                Back
              </Button>
              <Button
                variant="outline"
                onClick={handleSkip}
                className="flex-1 min-w-0 text-sm px-2"
              >
                Skip
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 min-w-0 text-sm px-2"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1 flex-shrink-0" />
              </Button>
            </div>
          </div>
        )}

        {/* Final Step: Review & Submit */}
        {currentStep === totalSteps && (
          <div className="fixed inset-0 bg-gradient-to-b from-slate-50 to-white flex flex-col">
            {/* Fixed Header with Exit */}
            <div className="flex-shrink-0 pt-6 px-4 pb-4">
              <div className="flex justify-end max-w-md mx-auto mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/blood-results')}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Exit
                </Button>
              </div>
              <div className="text-center mb-4">
                <p className="text-sm text-slate-500 mb-2">Step {currentStep} of {totalSteps}</p>
                <h1 className="text-xl font-bold text-slate-900">Review Blood Test</h1>
                <p className="text-sm text-slate-600">Check all values before submitting</p>
              </div>

              {/* Test Date */}
              <div className="bg-primary/5 rounded-xl p-3 border border-primary/20 max-w-md mx-auto">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <div>
                      <p className="text-xs text-slate-600">Test Date</p>
                      <p className="text-base font-bold text-slate-900">
                        {formatDisplayDate(formData.date, user?.country || 'AU')}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setCurrentStep(1)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Scrollable Markers */}
            <div className="flex-1 overflow-y-auto px-4">
              <div className="bg-white rounded-2xl p-3 border border-slate-200 space-y-2 max-w-md mx-auto">
                {markers.map((marker) => {
                  const hasValue = formData[marker.field] !== '';
                  return (
                    <div
                      key={marker.field}
                      className={`flex items-center justify-between p-2 rounded-lg ${
                        hasValue ? 'bg-slate-50' : 'bg-slate-50/50'
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-600">{marker.label}</p>
                        {hasValue ? (
                          <p className="text-base font-bold text-slate-900">
                            {formData[marker.field]} <span className="text-xs text-slate-500">{getMarkerUnit(marker)}</span>
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400">Not entered</p>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleEditMarker(marker.step)}>
                        <Edit className="h-3 w-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>

              {/* Illness Checkbox */}
              <div className="bg-white rounded-2xl p-4 border border-slate-200 mt-3 max-w-md mx-auto">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_during_illness}
                    onChange={(e) => setFormData({ ...formData, is_during_illness: e.target.checked })}
                    className="mt-1 h-5 w-5 rounded border-slate-300 text-amber-500 focus:ring-amber-500"
                    data-testid="illness-checkbox"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Thermometer className="h-4 w-4 text-amber-500" />
                      <span className="font-medium text-slate-900 text-sm">I was unwell around this test</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Tick this if you had an infection, cold, or were fighting illness. This reading will be marked and excluded from trend calculations.
                    </p>
                  </div>
                </label>
              </div>

              {/* Confidence warnings for scanned values */}
              {confidenceData && Object.values(confidenceData).some(v => v?.needs_review) && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-3 max-w-md mx-auto">
                  <p className="text-xs text-amber-800 font-medium mb-2">Please check these values:</p>
                  <ul className="text-xs text-amber-700 space-y-1">
                    {Object.entries(confidenceData).map(([key, value]) => {
                      if (value?.needs_review) {
                        const marker = markers.find(m => m.field === key);
                        return (
                          <li key={key} className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                            {marker?.label || key}: {value.calculated ? 'Calculated from percentage — please verify' : 'Low confidence — please verify'}
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                </div>
              )}

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mt-3 max-w-md mx-auto">
                <p className="text-xs text-amber-800">
                  This will create one blood test record with all the markers you&apos;ve entered.
                </p>
              </div>
            </div>

            {/* Fixed Footer Buttons */}
            <div className="flex-shrink-0 p-4 pb-24 bg-white border-t border-slate-200">
              <div className="flex gap-3 max-w-md mx-auto">
                <Button variant="ghost" onClick={handleBack} className="flex-1 min-w-0">
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
                <Button onClick={handleSubmit} className="flex-1 min-w-0 bg-green-600 hover:bg-green-700">
                  <Check className="h-4 w-4 mr-1" />
                  Submit
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepByStepBloodEntry;
